/**
 * Creates a normalized URL path from an array of path segments.
 * Removes duplicate slashes and trailing slashes, and replaces wildcards.
 *
 * @param {string[]} path - Array of path segments to join.
 * @returns {string} The normalized path string.
 */
export function createLink(path) {
    return path
        .map((p) => p.replace(/\/\*/, '/'))
        .join('/')
        .replace(/\/\/+/g, '/')
        .replace(/\/+$/, '');
}
/**
 * Checks if a full page path matches a subpath or dynamic route pattern.
 *
 * @param {string} fullPagePath - The full path of the page (e.g., from location).
 * @param {string} subPath - The sub-path or base path to check against.
 * @param {boolean} [index] - Whether the route is an index route.
 * @param {string} [routePath] - Optional route path with potential dynamic segments (e.g., ':id').
 * @returns {boolean} True if the path matches the given subPath or route pattern.
 */
function pathMatches(fullPagePath, subPath, index, routePath) {
    if (subPath.endsWith('/') && !fullPagePath.endsWith('/')) {
        fullPagePath += '/';
    }
    if (fullPagePath === subPath) {
        return true;
    }
    if (routePath && routePath.indexOf(':') > -1) {
        const pathRegex = new RegExp(routePath.replace(/:[\w*]+/g, '.*'));
        if (pathRegex.test(fullPagePath)) {
            return true;
        }
    }
    return !index && fullPagePath.startsWith(subPath);
}
/**
 * Maps route objects to item objects and determines which item is selected based on the current path.
 *
 * @template RouteType - The route object type, which may include path, index, and children.
 * @template ItemType - The item type that includes `id` and optionally `base`.
 * @param {string} fullPagePath - The full path to compare against route base paths.
 * @param {RouteType[]} routes - The array of route objects to traverse.
 * @param {(route: RouteType) => ItemType | undefined} routeToItem - A function mapping a route to an item.
 * @returns {[ItemType[], string | undefined]} A tuple: the array of items and the selected item ID (if any).
 */
export function mapRoutesToItemsAndFindSelected(fullPagePath, routes, routeToItem) {
    const items = [];
    let selectedId = undefined;
    const forEachRouteRecursive = (routes) => {
        for (const route of routes) {
            const item = routeToItem(route);
            if (item) {
                items.push(item);
                if (item.base != null && pathMatches(fullPagePath, item.base, route.index, route.path)) {
                    if (route.path != null || selectedId == null) {
                        selectedId = item.id;
                    }
                }
            }
            else if (route.children) {
                forEachRouteRecursive(route.children);
            }
        }
    };
    forEachRouteRecursive(routes);
    return [items, selectedId];
}
/**
 * Returns a navigation callback function to be used in event handlers like onClick.
 *
 * @param {NavigationInterface} navigationContext - The context managing the navigation flow.
 * @param {NavigateFunction} navigate - The navigate function from React Router.
 * @param {string} [navigateTo] - Optional path to navigate to directly.
 * @param {(navigate: NavigateFunction) => void} [onClick] - Optional custom click handler using the navigate function.
 * @returns {(() => void) | undefined} A function to be used in a click event, or undefined if no navigation is required.
 */
export function navigateToCallback(navigationContext, navigate, navigateTo, onClick) {
    if (onClick) {
        return () => navigationContext.navigate(() => onClick(navigate));
    }
    else if (navigateTo != null) {
        return () => navigationContext.navigate(() => navigate(navigateTo));
    }
    else {
        return undefined;
    }
}
